/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {signal} from '@preact/signals-react';
import {getFeatureFlags} from '@ion/applications/src/routes/RouteUtils';
import {createMutator} from '@ion/applications/src/store/SignalUtils';
import {getLocalStorage, setLocalStorage} from '@ion/core/src/capabilities/storage';
import {info} from '@ion/core/src/logger/logger';
import {isResellerHost} from '@ion/dls/src/theme/resellers/Resellers';
import {currentTheme, hasStoredTheme, setCurrentTheme} from '@ion/dls/src/theme/ThemeStore';
import {DLSThemes} from '@ion/dls/src/theme/types';
import {FeatureFlag} from './FeatureFlags';
import {addFlagToSearch, isLocalPath} from './featureFlagUrl';

// remembers in the browser that the `dls` Feature Flag was given, so access
// outlives the query string - never deleted, clearing site data resets it ...
const FEATURE_FLAG_STORAGE_KEY = 'ion-dls-feature-flag';

/**
 * Tracks whether the New Theme Toggle is enabled: the `dls` Feature Flag
 * (`?features=dls`) was given when the application loaded, or the browser
 * remembered it from an earlier visit. It is held for the life of the page,
 * so an in-app link or a redirect that drops the query string keeps the New
 * Theme Toggle; a reload reads the query string and the remembered access
 * again.
 */
export const newThemeToggleEnabled = signal<boolean>(false);

/**
 * Mutator function for enabling or disabling the New Theme Toggle.
 */
export const setNewThemeToggleEnabled = createMutator(newThemeToggleEnabled);

/**
 * Determines if the user may switch between the legacy and current themes -
 * only while the `dls` Feature Flag was given when the application loaded, or
 * the browser remembered it from an earlier visit. No role grants it; without
 * it, neither theme's switch is shown and the user stays on the legacy theme.
 *
 * ```typescript
 *
 *   import {hasNewThemeToggleAccess} from '@/security/themeAccess';
 *   ...
 *
 *   const canSwitchTheme = hasNewThemeToggleAccess();
 *
 * ```
 *
 * @returns `true` when the `dls` Feature Flag was given or remembered,
 *          otherwise `false`.
 */
export const hasNewThemeToggleAccess = (): boolean => newThemeToggleEnabled.value;

/**
 * Determines if the dark mode toggle may be shown - the user has the New
 * Theme Toggle, and the application is not served from a reseller host, where
 * dark mode is never offered. It is the caller's own check of access: the
 * toggle itself also needs the current theme, and is not told of the user.
 *
 * ```typescript
 *
 *   import {canToggleThemeMode} from '@/security/themeAccess';
 *   ...
 *
 *   <ThemeModeToggle canToggle={canToggleThemeMode()} slot='nav' />
 *
 * ```
 *
 * @returns `true` when the dark mode toggle may be shown, otherwise `false`.
 */
export const canToggleThemeMode = (): boolean => hasNewThemeToggleAccess() && !isResellerHost();

// what the Feature Flags given and the access the browser remembers resolve
// to, before anything is changed ...
type ThemeAccess = {
  isFlagged: boolean;
  isEnabled: boolean;
  onResellerHost: boolean;
};

// resolves the access from the `dls` Feature Flag given, or the access the
// browser remembers, which a reseller host never reads ...
const resolveThemeAccess = (flags: string[]): ThemeAccess => {
  const isFlagged      = Boolean(flags?.includes(FeatureFlag.DLS));
  const onResellerHost = isResellerHost();
  const isRemembered   = !onResellerHost && getLocalStorage(FEATURE_FLAG_STORAGE_KEY) === 'true';
  return {isFlagged, isEnabled: isFlagged || isRemembered, onResellerHost};
};

// remembers the access in the browser once the flag is given, never on a
// reseller host ...
const rememberThemeAccess = ({isFlagged, onResellerHost}: ThemeAccess): void => {
  if (isFlagged && !onResellerHost) {
    setLocalStorage(FEATURE_FLAG_STORAGE_KEY, 'true');
  }
};

// settles the theme: the legacy theme without access, the current theme on a
// reseller host with it, and with access elsewhere the saved theme, or the
// current theme when none is saved ...
const settleTheme = ({isEnabled, onResellerHost}: ThemeAccess): void => {
  const theme = isEnabled ? DLSThemes.current : DLSThemes.legacy;
  if (!isEnabled || onResellerHost) {
    if (currentTheme.value !== theme) {
      setCurrentTheme(theme);
    }
  } else if (!hasStoredTheme()) {
    setCurrentTheme(DLSThemes.current);
  }
};

/**
 * Settles the New Theme Toggle and the theme from the query string's Feature
 * Flags and the access the browser remembers, once, as the application loads
 * and before its first render, so the login page already shows the theme the
 * user will have. Given the `dls` Feature Flag, access is remembered in the
 * browser (never on a reseller host), so it outlives the query string. With
 * access, the user's saved theme is kept, and the current theme is used only
 * when none is saved; on a reseller host, where the switch is hidden, the
 * `dls` Feature Flag still starts the current theme. Without access, the
 * application starts in the legacy theme, with no toggle.
 *
 * ```typescript
 *
 *   import {initializeThemeAccess} from '@/security/themeAccess';
 *   ...
 *
 *   // https://ion.calamp.com/login?features=dls ...
 *   initializeThemeAccess(getFeatureFlags('features'));
 *
 * ```
 *
 * @param flags The Feature Flags given in the query string.
 */
export const initializeThemeAccess = (flags: string[]): void => {
  const access = resolveThemeAccess(flags);
  rememberThemeAccess(access);
  setNewThemeToggleEnabled(access.isEnabled);
  settleTheme(access);
};

/**
 * Defines the address the browser is to show, in the parts a router takes.
 */
export type ThemeFeatureFlagLocation = {
  pathname: string;
  search: string;
  hash: string;
};

/**
 * Provides the address the browser is to show once the application has
 * loaded, when the New Theme Toggle is enabled but the `dls` Feature Flag is
 * missing from the query string - remembered access has no flag in the URL,
 * and Next.js rewrites the URL as it loads. The address is the current path
 * with `dls` added to the `features` parameter; every other parameter, its
 * encoding, and the hash are kept as they are. Never given on a reseller
 * host, nor for a path which begins with `//`, which a browser would read as
 * another host.
 *
 * ```typescript
 *
 *   import {getThemeFeatureFlagLocation} from '@/security/themeAccess';
 *   ...
 *
 *   // https://ion.calamp.com/login?features=apm#top ...
 *   getThemeFeatureFlagLocation();
 *   // {pathname: '/login', search: '?features=apm,dls', hash: '#top'}
 *
 * ```
 *
 * @returns The path, query string, and hash with the `dls` Feature Flag
 *          added, or `undefined` when there is nothing to add.
 */
export const getThemeFeatureFlagLocation = (): ThemeFeatureFlagLocation | undefined => {
  const {pathname, search, hash} = window.location;
  const isMissing                = canToggleThemeMode() && !getFeatureFlags('features').includes(FeatureFlag.DLS);
  return isMissing && isLocalPath(pathname) ? {pathname, search: addFlagToSearch(search), hash} : undefined;
};

/**
 * Holds the signed-in user to the legacy theme unless the `dls` Feature Flag
 * was given when the application loaded, or the browser remembered it - should
 * anything have moved to the current theme before sign-in without it, the
 * user is returned to the legacy theme. Call it right after the user's
 * features are initialized.
 *
 * ```typescript
 *
 *   import {verifyThemeAccess} from '@/security/themeAccess';
 *   ...
 *
 *   Features.initialize({...});
 *   verifyThemeAccess();
 *
 * ```
 *
 * @returns `true` when the user was returned to the legacy theme, otherwise
 *          `false`.
 */
export const verifyThemeAccess = (): boolean => {
  const isReturned = currentTheme.value === DLSThemes.current && !hasNewThemeToggleAccess();
  if (isReturned) {
    info('The current theme is not permitted without the dls feature flag - returning to the legacy theme');
    setCurrentTheme(DLSThemes.legacy);
  }
  return isReturned;
};

export default verifyThemeAccess;
